import _isEqual from 'lodash/isEqual';
import {
  AddPageContentItemAction,
  ClearImageAction,
  DeletePageContentItemAction,
  MovePageContentItemBackwardAction,
  MovePageContentItemForwardAction,
  MoveTextContentToCacheAction,
  ReplaceLayoutImageItemsAction,
  UpdateContentItemAction,
  UpdateContentItemContainerLocationAction,
  UpdateContentItemContentLocationAction,
  UpdateContentItemLocationsAction,
  UpdateImageContentItemZoomAction,
  UpdateTextContentItemContentAction,
  UpdateTextContentItemBackgroundColorAction
} from '@src/actions/wysiwyg/editor';
import {
  PAGE_CONTENT_ITEM_TYPE_AUTHOR_NAME,
  PAGE_CONTENT_ITEM_TYPE_CAPTION_TEXT,
  PAGE_CONTENT_ITEM_TYPE_IMAGE,
  PAGE_CONTENT_ITEM_TYPE_RECTANGLE,
  PAGE_CONTENT_ITEM_TYPE_TEXT_BOX
} from '@src/constants/wysiwyg_editor';
import type {
  TAuthorNameContentItem,
  TCaptionTextContentItem,
  TImageContentItem,
  TPageContentItem,
  TRectangleContentItem,
  TTextBoxContentItem
} from '@src/types/wysiwyg_editor';

export function isRectangle(item: TPageContentItem): item is TRectangleContentItem {
  return item.type === PAGE_CONTENT_ITEM_TYPE_RECTANGLE;
}

export function isAttribution(item: TPageContentItem): item is TAuthorNameContentItem {
  return item.type === PAGE_CONTENT_ITEM_TYPE_AUTHOR_NAME;
}

export function isCaption(item: TPageContentItem): item is TCaptionTextContentItem {
  return item.type === PAGE_CONTENT_ITEM_TYPE_CAPTION_TEXT;
}

export function isImage(item: TPageContentItem | undefined | null): item is TImageContentItem {
  if (!item) return false;
  return item.type === PAGE_CONTENT_ITEM_TYPE_IMAGE;
}

export function isTextBox(item: TPageContentItem | null): item is TTextBoxContentItem {
  return item?.type === PAGE_CONTENT_ITEM_TYPE_TEXT_BOX;
}

export function areAllImagesPopulated(contentItems: TPageContentItem[]): boolean {
  return contentItems.filter(isImage).every((item) => {
    const href = item.href;

    return href && href.length > 0;
  });
}

export function enforcePageContentOrder(items: Array<TPageContentItem>): Array<TPageContentItem> {
  const attributionItems = items.filter((item) => item.type === PAGE_CONTENT_ITEM_TYPE_AUTHOR_NAME);
  const captionItems = items.filter((item) => item.type === PAGE_CONTENT_ITEM_TYPE_CAPTION_TEXT);
  const imageOrTextItems: TPageContentItem[] = items.filter((item) => {
    return item.type === PAGE_CONTENT_ITEM_TYPE_IMAGE || item.type === PAGE_CONTENT_ITEM_TYPE_TEXT_BOX;
  });
  const otherItems = items.filter((item) => {
    const isAttribution = attributionItems.some((el) => el === item);
    const isCaption = captionItems.some((el) => el === item);
    const isImageOrText = imageOrTextItems.some((el) => el === item);

    return !isAttribution && !isCaption && !isImageOrText;
  });

  return [...imageOrTextItems, ...attributionItems, ...captionItems, ...otherItems];
}

export function updatePageContentItem(
  items: TPageContentItem[],
  id: string,
  handler: (item: TPageContentItem) => TPageContentItem
): TPageContentItem[] {
  let itemChanged = false;

  const newItems = items.map<TPageContentItem>((item) => {
    if (item.id === id) {
      const updatedItem = handler(item);
      itemChanged = !_isEqual(updatedItem, item);

      // we can return either item if the item wasn't changed, but returning the
      // original for more explicit code
      return itemChanged ? updatedItem : item;
    } else {
      return item;
    }
  });

  return itemChanged ? newItems : items;
}

export function doesItemSeemValid(item: TPageContentItem): boolean {
  if (!item?.hasOwnProperty('type')) return false;
  if (!item?.hasOwnProperty('id')) return false;
  if (!item?.containerLocation.hasOwnProperty('x')) return false;
  if (!item?.containerLocation.hasOwnProperty('y')) return false;
  if (!item?.containerLocation.hasOwnProperty('height')) return false;
  if (!item?.containerLocation.hasOwnProperty('width')) return false;

  return true;
}

type AllowedActions =
  | AddPageContentItemAction
  | DeletePageContentItemAction
  | MovePageContentItemBackwardAction
  | MovePageContentItemForwardAction
  | ReplaceLayoutImageItemsAction
  | UpdateContentItemContainerLocationAction
  | UpdateContentItemContentLocationAction
  | UpdateContentItemLocationsAction
  | UpdateImageContentItemZoomAction
  | UpdateContentItemAction
  | UpdateTextContentItemContentAction
  | UpdateTextContentItemBackgroundColorAction
  | ClearImageAction
  | MoveTextContentToCacheAction;

type Keys =
  | 'WYSIWYG_EDITOR_ADD_PAGE_CONTENT_ITEM'
  | 'WYSIWYG_EDITOR_DELETE_PAGE_CONTENT_ITEM'
  | 'WYSIWYG_EDITOR_MOVE_PAGE_CONTENT_ITEM_BACKWARD'
  | 'WYSIWYG_EDITOR_MOVE_PAGE_CONTENT_ITEM_FORWARD'
  | 'WYSIWYG_EDITOR_REPLACE_PAGE_CONTENT_IMAGES'
  | 'WYSIWYG_EDITOR_UPDATE_CONTENT_ITEM_CONTAINER_LOCATION'
  | 'WYSIWYG_EDITOR_UPDATE_CONTENT_ITEM_CONTENT_LOCATION'
  | 'WYSIWYG_EDITOR_UPDATE_CONTENT_ITEM_LOCATIONS'
  | 'WYSIWYG_EDITOR_UPDATE_IMAGE_PAGE_CONTENT_ITEM_ZOOM'
  | 'WYSIWYG_EDITOR_UPDATE_PAGE_CONTENT_ITEM'
  | 'WYSIWYG_EDITOR_UPDATE_TEXT_PAGE_CONTENT_ITEM_CONTENT'
  | 'WYSIWYG_EDITOR_UPDATE_TEXT_PAGE_CONTENT_ITEM_BACKGROUND_COLOR'
  | 'WYSIWYG_EDITOR_CLEAR_IMAGE'
  | 'WYSIWYG_EDITOR_MOVE_TEXT_CONTENT_TO_CACHE';

export const contentItemModifiers: Record<
  Keys,
  (items: Array<TPageContentItem>, action: AllowedActions) => Array<TPageContentItem>
> = {
  WYSIWYG_EDITOR_ADD_PAGE_CONTENT_ITEM(items: Array<TPageContentItem>, action: AddPageContentItemAction) {
    const newItems = [...items, action.payload];

    return enforcePageContentOrder(newItems);
  },
  WYSIWYG_EDITOR_DELETE_PAGE_CONTENT_ITEM(
    items: Array<TPageContentItem>,
    action: DeletePageContentItemAction
  ): Array<TPageContentItem> {
    return items.filter((item) => item.id !== action.payload);
  },
  WYSIWYG_EDITOR_MOVE_PAGE_CONTENT_ITEM_BACKWARD(
    items: Array<TPageContentItem>,
    action: MovePageContentItemBackwardAction
  ): Array<TPageContentItem> {
    const itemIndex = items.findIndex((item) => item.id === action.payload);

    if (itemIndex === -1) return items; // item wasn't found
    if (itemIndex === 0) return items; // don't move item to index -1

    const item = items[itemIndex];
    const newItems = [...items];
    newItems.splice(itemIndex, 1);
    newItems.splice(itemIndex - 1, 0, item);

    return enforcePageContentOrder(newItems);
  },
  WYSIWYG_EDITOR_MOVE_PAGE_CONTENT_ITEM_FORWARD(
    items: Array<TPageContentItem>,
    action: MovePageContentItemForwardAction
  ): Array<TPageContentItem> {
    const itemIndex = items.findIndex((item) => item.id === action.payload);

    if (itemIndex === -1) return items;

    const item = items[itemIndex];
    const newItems = [...items];
    newItems.splice(itemIndex, 1);
    newItems.splice(itemIndex + 1, 0, item);

    return enforcePageContentOrder(newItems);
  },
  WYSIWYG_EDITOR_REPLACE_PAGE_CONTENT_IMAGES(
    items: Array<TPageContentItem>,
    action: ReplaceLayoutImageItemsAction
  ): Array<TPageContentItem> {
    const currentNonImageOrTextItems = items.filter((item) => {
      return item.type !== PAGE_CONTENT_ITEM_TYPE_IMAGE && item.type !== PAGE_CONTENT_ITEM_TYPE_TEXT_BOX;
    });
    const newImageItems = action.payload.filter((item) => item.type === PAGE_CONTENT_ITEM_TYPE_IMAGE);

    const newTextItems = action.payload.filter((item) => item.type === PAGE_CONTENT_ITEM_TYPE_TEXT_BOX);

    return [...currentNonImageOrTextItems, ...newImageItems, ...newTextItems];
  },
  WYSIWYG_EDITOR_UPDATE_CONTENT_ITEM_CONTAINER_LOCATION(
    items: Array<TPageContentItem>,
    action: UpdateContentItemContainerLocationAction
  ): Array<TPageContentItem> {
    return updatePageContentItem(items, action.payload.id, (item) => ({
      ...item,
      containerLocation: action.payload.newLocation
    }));
  },
  WYSIWYG_EDITOR_UPDATE_CONTENT_ITEM_CONTENT_LOCATION(
    items: Array<TPageContentItem>,
    action: UpdateContentItemContentLocationAction
  ): Array<TPageContentItem> {
    return updatePageContentItem(items, action.payload.id, (item) => ({
      ...item,
      contentLocation: action.payload.newLocation
    }));
  },
  WYSIWYG_EDITOR_UPDATE_CONTENT_ITEM_LOCATIONS(
    items: Array<TPageContentItem>,
    action: UpdateContentItemLocationsAction
  ): Array<TPageContentItem> {
    return updatePageContentItem(items, action.payload.id, (item) => ({
      ...item,
      containerLocation: action.payload.newContainerLocation,
      contentLocation: action.payload.newContentLocation
    }));
  },
  WYSIWYG_EDITOR_UPDATE_IMAGE_PAGE_CONTENT_ITEM_ZOOM(
    items: Array<TPageContentItem>,
    action: UpdateImageContentItemZoomAction
  ): Array<TPageContentItem> {
    return updatePageContentItem(items, action.payload.id, (item) => ({
      ...item,
      zoom: action.payload.newZoom
    }));
  },
  WYSIWYG_EDITOR_UPDATE_PAGE_CONTENT_ITEM(
    items: Array<TPageContentItem>,
    action: UpdateContentItemAction
  ): Array<TPageContentItem> {
    return enforcePageContentOrder(
      updatePageContentItem(items, action.payload.id, (item) => {
        if (_isEqual(item, action.payload.newData)) {
          return item;
        }

        return action.payload.newData;
      })
    );
  },
  WYSIWYG_EDITOR_UPDATE_TEXT_PAGE_CONTENT_ITEM_CONTENT(
    items: Array<TPageContentItem>,
    action: UpdateTextContentItemContentAction
  ): Array<TPageContentItem> {
    return updatePageContentItem(items, action.payload.id, (item) => ({
      ...item,
      content: action.payload.content
    }));
  },
  WYSIWYG_EDITOR_UPDATE_TEXT_PAGE_CONTENT_ITEM_BACKGROUND_COLOR(
    items: Array<TPageContentItem>,
    action: UpdateTextContentItemBackgroundColorAction
  ): Array<TPageContentItem> {
    return updatePageContentItem(items, action.payload.id, (item) => ({
      ...item,
      backgroundColor: action.payload.color
    }));
  },
  WYSIWYG_EDITOR_CLEAR_IMAGE(items: Array<TPageContentItem>, action: ClearImageAction): Array<TPageContentItem> {
    return updatePageContentItem(items, action.payload, (item) => ({
      ...item,
      href: null,
      zoom: 1,
      contentLocation: {}
    }));
  },
  WYSIWYG_EDITOR_MOVE_TEXT_CONTENT_TO_CACHE(
    items: Array<TPageContentItem>,
    action: MoveTextContentToCacheAction
  ): Array<TPageContentItem> {
    return updatePageContentItem(items, action.payload.id, (item) => ({
      ...item,
      content: null
    }));
  }
};
